import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Check, ShoppingBag, Shield, TrendingUp, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { packages } from '../data/mock';
import CTAButtons from '../components/CTAButtons';
import RegistrationModal from '../components/RegistrationModal';

const AmazonBrandRegistry = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  const benefits = [
    {
      icon: Shield,
      title: 'Brand Protection',
      description: 'Powerful tools to protect your brand and remove counterfeit or infringing products from Amazon.'
    },
    {
      icon: Search,
      title: 'Enhanced Content',
      description: 'Access to A+ Content, brand stores, and sponsored brand ads to showcase your products.'
    },
    {
      icon: TrendingUp,
      title: 'Increased Sales',
      description: 'Better product listings and brand visibility lead to higher conversion rates and sales.'
    },
    {
      icon: ShoppingBag,
      title: 'Exclusive Features',
      description: 'Access Amazon Brand Registry-only features like automated brand protection and transparency codes.'
    }
  ];

  const requirements = [
    'Active registered trademark with USPTO (or trademark application pending)',
    'Ability to verify you are the rights owner or authorized representative',
    'Amazon seller or vendor account',
    'Products with trademark visible on product or packaging'
  ];

  const features = [
    'Proactive brand protection with automated monitoring',
    'Predictive automation to remove suspected infringement',
    'Increased authority over product listings',
    'Access to stores for immersive brand experience',
    'Enhanced marketing content (A+ Content)',
    'Sponsored Brands advertising eligibility',
    'Brand Analytics for customer insights',
    'Transparency serial number program'
  ];

  return (
    <div>
      <Helmet>
        <title>Amazon Brand Registry Services - Protect Your Amazon Brand | Global Trademark USA</title>
        <meta name="description" content="Enroll in Amazon Brand Registry with our expert assistance. Protect your products, gain control of listings, and access powerful brand protection tools on Amazon." />
        <meta name="keywords" content="Amazon Brand Registry, Amazon brand protection, Amazon seller protection, brand registry enrollment, Amazon trademark" />
        <link rel="canonical" href="https://yourdomain.com/amazon-brand-registry" />
      </Helmet>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-emerald-50 via-white to-blue-50 py-20">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block mb-4 px-4 py-2 bg-emerald-100 text-emerald-800 rounded-full text-xl font-semibold">
                Amazon Brand Registry
              </div>
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
                Protect Your Brand on Amazon
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Gain control of your brand on Amazon's marketplace. Access powerful tools to protect your products and enhance your presence.
              </p>
              <CTAButtons buttonText="Start Brand Registry" />
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf"
                alt="Amazon Brand Registry"
                className="rounded-2xl shadow-2xl w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* What is Amazon Brand Registry */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-6 text-center">What is Amazon Brand Registry?</h2>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              Amazon Brand Registry is a program that helps brand owners protect their intellectual property and create a better customer experience on Amazon. It provides powerful tools to detect and remove potentially infringing or inaccurate content.
            </p>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              By enrolling in Amazon Brand Registry, you gain access to enhanced marketing content, better search results, and proactive protection against counterfeit products and unauthorized sellers.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 bg-gradient-to-br from-gray-50 to-emerald-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Why Enroll in Amazon Brand Registry?
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div key={index} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all hover:-translate-y-2">
                  <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-blue-500 rounded-xl flex items-center justify-center mb-4">
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Requirements */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
              Enrollment Requirements
            </h2>
            <div className="bg-gradient-to-br from-emerald-50 to-blue-50 rounded-2xl p-8 shadow-xl">
              <div className="space-y-4">
                {requirements.map((req, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <Check className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-1" />
                    <p className="text-lg text-gray-700 leading-relaxed">{req}</p>
                  </div>
                ))}
              </div>
              <div className="mt-8 p-6 bg-white rounded-xl border-2 border-emerald-200">
                <p className="text-gray-700 font-semibold">
                  <strong>Important:</strong> You must have a registered trademark to enroll in Amazon Brand Registry. If you don't have one yet, we can help you register your trademark first!
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
              Features & Benefits
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {features.map((feature, index) => (
                <div key={index} className="flex items-start gap-3 bg-white p-6 rounded-xl shadow-lg">
                  <Check className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700 leading-relaxed font-medium">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Amazon Brand Registry Packages
            </h2>
            <p className="text-xl text-gray-600">We handle the entire enrollment process for you</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {packages.amazon.map((pkg, index) => (
              <Card key={index} className="hover:shadow-xl transition-all duration-300 hover:border-emerald-200">
                <CardHeader>
                  <CardTitle className="text-2xl">{pkg.name}</CardTitle>
                  <CardDescription className="text-3xl font-bold text-gray-900">
                    Starting at ${pkg.price}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="feature-list space-y-0">
                    {pkg.features.map((feature, idx) => (
                      <li key={idx}>
                        <Check className="w-5 h-5 text-emerald-600" />
                        <span className="text-base text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
                <CardFooter>
                  <Button onClick={() => setIsModalOpen(true)} className="w-full bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800">
                    Start Brand Registry
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-br from-blue-900 via-emerald-900 to-blue-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Protect Your Amazon Brand?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Let us help you enroll in Amazon Brand Registry and take control of your brand.
          </p>
          <CTAButtons className="justify-center" buttonText="Start Brand Registry" variant="dark" />
        </div>
      </section>
      <RegistrationModal open={isModalOpen} onOpenChange={setIsModalOpen} />
    </div>
  );
};

export default AmazonBrandRegistry;